/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

import is from 'is_js';
import {readCookie} from '../utils';
import {store} from '../client-create-app';
import {
  request,
  cancelRequests,
  send,
  subscribe,
  unsubscribe,
  bulkSubscribe,
  bulkUnsubscribe,
} from '../sockets';

/**
 * Send a subscribe request to the server.
 * @param  {Function} commit Commit a mutation.
 * @param  {String} channel        Channel to subscribe to.
 */
export const sub = ({commit}, channel) => {
  subscribe(channel);
};

/**
 * Send an unsubscribe request to the server.
 * @param  {Function} commit Commit a mutation.
 * @param  {String} channel        Channel to unsubscribe from.
 */
export const unsub = ({commit}, channel) => {
  unsubscribe(channel);
};

/**
 * Send a bulk subscribe request to the server.
 * @param  {Function} commit Commit a mutation.
 * @param  {String} channel        Channel to subscribe to.
 */
export const bulkSub = ({commit}, channels) => {
  bulkSubscribe(channels);
};

/**
 * Send an bulk unsubscribe request to the server.
 * @param  {Function} commit Commit a mutation.
 * @param  {String} channel        Channel to unsubscribe from.
 */
export const bulkUnsub = ({commit}, channels) => {
  bulkUnsubscribe(channels);
};

/**
 * Cancel requests for the passed tab/view.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.tab    Tab context.
 * @param  {String} options.view   View context.
 */
export const cancelSocketRequests = ({commit}, {tab, view}) => {
  cancelRequests(tab, view);
};

/**
 * Send request to the server to log the user in.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.email  E-mail from input.
 * @param  {String} options.pass   Password from input.
 * @param  {Object} options.social   (optional) Social login type and id.
 * @param  {Object} options.secret   (optional) Secret key for multi-factor auth.
 * @param  {Object} options.code   (optional) The multi-factor auth code.
 */
export const login = ({commit}, {email, pass, social, secret, code}) => {
  return new Promise((resolve) => {
    request('auth', 'login', {email, pass, social, secret, code}, (data) => {
      if (!data.err && !data.signup && !data.multiFactor) {
        commit('processLogin', {email, data});
      }

      resolve(data);
    });
  });
};

/**
 * Get the new session data from the server and update the client.
 * @param  {Function} options.commit Perform a mutation.
 */
export const reAuth = ({commit}) => {
  request('auth', 'auth', {}, (data) => {
    commit('processLogin', {email: '', data});
  });
};

/**
 * Send a request to the server to send a password reset e-mail.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} email          E-mail of account.
 * @return {Promise}
 */
export const resetPassword = ({commit}, email) => {
  return new Promise((resolve) => {
    request('auth', 'reset', {email}, (data) => {
      resolve(data);
    });
  });
};

/**
 * Send a message to the server to update this user's e-mail address.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} email          New e-mail address.
 */
export const updateEmail = ({commit}, email) => {
  send('auth', 'updateEmail', {email});
};

/**
 * Send a request to the server to verify an account.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.user   Username to verify.
 * @param  {String} options.key    Unique key.
 * @param  {String} options.bday   Birthday.
 * @param  {String} options.gender  Gender.
 * @return {Promise}
 */
export const verifyAccount = ({commit}, {user, key, bday, gender}) => {
  return new Promise((resolve) => {
    request('auth', 'verify', {user, key, bday, gender}, (data) => {
      if (data.cookie && !data.error && !data.badAge) {
        commit('processLogin', {email: data.email, data});
      }

      resolve(data);
    });
  });
};

/**
 * Send a request to the server to check if this player is a guest.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.user   Username to verify.
 * @return {Promise}
 */
export const checkGuest = ({commit}, {user}) => {
  return new Promise((resolve) => {
    request('auth', 'checkGuest', {user}, (data) => {
      resolve(data);
    });
  });
};

/**
 * Called when clicking the back button to switch back to the previous view.
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {Object}   state           Full state reference.
 */
export const goBackView = ({commit, state}) => {
  const lastView = state.panel.history[state.panel.history.length - 1];

  if (!lastView) {
    return;
  }

  commit('popHistory'); // Remove last view from history.

  // Apply the last view.
  if (typeof lastView === 'string') {
    commit('setView', lastView);
  } else if (typeof lastView === 'object') {
    if (lastView.mutation) {
      commit(lastView.mutation, lastView.data);
    } else {
      store.dispatch(lastView.action, lastView.data);
    }
  }
};

/**
 * Load profile data for a user (default to self).
 * @param  {Function} options.commit            Perform a mutation.
 * @param  {Object}   options.state             Full state reference.
 * @param  {String}   options.uid               User for which to get data.
 * @param  {String}   options.user              Username for which to get data.
 * @param  {String}   options.shouldAddHistory  Whether this view should be stored in panel history.
 * @return {Promise}
 */
export const viewProfile = ({commit, state}, {uid, user, shouldAddHistory = true}) => {
  commit('scrollToTop'); // In case user is visiting profile from another profile and is scrolled.
  commit('togglePanelLoading', true);

  // Capture previously viewed profile if it exists.
  const prevUid = state.launcher.profile.uid;

  // Reset the profile data.
  commit('setProfileData', {});

  // Make sure we are on the launcher tab.
  if (state.user.auth) {
    if (state.toolbar.activeTab !== 'launcher') {
      commit('toggleTab', 'launcher');
    }

    // Make sure we are on the launcher view.
    if (state.launcher.activeView !== 'profile-view') {
      if (shouldAddHistory) {
        commit('toggleTabView', {tab: 'launcher', view: 'profile-view'});
      } else {
        commit('setTabView', {tab: 'launcher', view: 'profile-view'});
      }
    } else {
      if (shouldAddHistory && prevUid) {
        // If user clicks back button, go to prior profile, but do not add a new history item.
        commit('addHistory', {action: 'viewProfile', data: {uid: prevUid, shouldAddHistory: false}});
      }
    }
  }

  // Get the profile data from the server.
  request('profiles', 'getProfile', {uid, user}, (data) => {
    commit('setProfileData', data);

    // Show error message.
    if (data.error) {
      commit('setPanelData', {
        title: 'USER PROFILE',
        buttons: [],
      });
      commit('togglePanelLoading', false);
      return;
    }

    uid = uid || data.uid;

    const isOwnProfile = (() => {
      if (user) {
        return (user === state.user.user);
      }
      if (uid) {
        return (uid === state.user.uid);
      }
      return false;
    })();

    const disabled = state.user.verify !== 1;
    let buttons = [{
      label: disabled ? 'Verify Email to Post' : 'New Post',
      disabled,
      view: {mutation: 'toggleEditor', data: {type: 'feed'}},
    }];
    if (!isOwnProfile) {
      buttons = [{
        label: disabled ? 'Unverified' : (data.isFriend ? 'Unfriend' : 'Add Friend'),
        disabled,
        view: {
          action: disabled ? null : (data.isFriend ? 'removeFriend' : 'addFriend'),
          data: {uid},
        },
      }];
    }

    if (!isOwnProfile && (data.isFriend && (state.user.role || data.isMutual || data.openchat))) {
      buttons.push({label: 'Message', view: {action: 'viewChatThread', data: {uid}}});
    }
    const footerView = isOwnProfile ? 'social-feed-footer' : undefined;

    commit('setPanelData', {
      title: 'USER PROFILE',
      buttons,
      footerView,
    });

    // Set statuses of this player and player's friends.
    commit('setPlayerStatus', [data, ...data.friends]);
    commit('togglePanelLoading', false);
  });
};

/**
 * Open a chat thread in the chat tab.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object}   options.uid    The ID of the chat message to view (if any).
 * @param  {Boolean} data.shouldAddHistory  Whether this view should be stored in panel history.
 */
export const viewChatThread = ({commit}, {id, uid, user, shouldAddHistory = true} = {}) => {
  commit('resetChatThread');

  if (id) {
    commit('viewChatMessage', {id, uid, user});
    if (shouldAddHistory) {
      commit('toggleTabView', {tab: 'chat', view: 'chat-message-view'});
    } else {
      commit('setTabView', {tab: 'chat', view: 'chat-message-view'});
    }
  } else if (uid) {
    request('chat', 'newThread', {uid}, (data) => {
      const {_id, uid, user} = data.chat;
      commit('viewChatMessage', {id: _id, uid, user});
      commit('toggleTabView', {tab: 'chat', view: 'chat-message-view'});
    });
  }
};

/**
 * Add user as a friend.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object}   options.state  Full state reference.
 * @param  {Object}   options.uid    The user ID of the user to add as a friend.
 */
export const addFriend = ({commit, state}, {uid}) => {
  const button = state.panel.buttons[0];

  button.loading = true;
  commit('setPanelData', state.panel);

  request('profiles', 'addFriend', {uid}, () => {
    button.loading = false;
    button.label = 'Unfriend';
    button.view.action = 'removeFriend';

    commit('setPanelData', state.panel);

    store.dispatch('viewProfile', {uid, shouldAddHistory: false});
  });
};

/**
 * Remove user as a friend.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object}   options.state  Full state reference.
 * @param  {Object}   options.uid    The user ID of the user to remove as a friend.
 */
export const removeFriend = ({commit, state}, {uid}) => {
  const button = state.panel.buttons[0];

  button.loading = true;
  commit('setPanelData', state.panel);

  request('profiles', 'removeFriend', {uid}, () => {
    button.loading = false;
    button.label = 'Add Friend';
    button.view.action = 'addFriend';

    commit('setPanelData', state.panel);

    store.dispatch('viewProfile', {uid, shouldAddHistory: false});
  });
};

/**
 * Send a request to the server to finish a password reset.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.pass   The new password.
 * @param  {String} options.user   Username.
 * @param  {String} options.key    Key to verify this user.
 * @return {Promise}
 */
export const finishResetPassword = ({commit}, {pass, user, key}) => {
  return new Promise((resolve) => {
    request('auth', 'finishReset', {pass, user, key}, (data) => {
      if (!data.errors || is.propertyCount(data.errors, 0)) {
        commit('processLogin', {email: data.email, data});
      }

      resolve(data);
    });
  });
};

/**
 * Log the user out. Reset client data, delete cookie and send request to server.
 * @param  {Function} options.commit Perform a mutation.
 */
export const logout = ({commit}) => {
  // Send message to server to log out.
  send('auth', 'logout', {});

  // Reset client data.
  commit('setUserData', {
    auth: false,
    user: '',
    avatar: '',
    id: '',
    games: [],
  });

  // Reset the session cookie.
  document.cookie = `sid=${readCookie('sid')}; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
};

/**
 * Send a request to the server to check if an e-mail is in use.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} email          The e-mail to check.
 */
export const checkEmail = ({commit}, email) => {
  return new Promise((resolve) => {
    request('auth', 'checkEmail', email, (data) => {
      resolve(data.err);
    });
  });
};

/**
 * Send a request to the server to complete a signup.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.email
 * @param  {String} options.pass
 * @param  {String} options.user
 * @param  {String} options.bday
 * @param  {String} options.gender
 */
export const signup = ({commit}, data) => {
  return new Promise((resolve) => {
    request('auth', 'signup', data, (res) => {
      if (is.propertyCount(res.errors, 0)) {
        commit('processLogin', {email: data.email, data: res});
      }

      resolve(res);
    });
  });
};

/**
 * Send a request to the server to complete a guest signup.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.user   Username.
 * @param  {String} options.src    Campaign source..
 * @return {Promise}
 */
export const guestSignup = ({commit}, {user, src}) => {
  return new Promise((resolve) => {
    request('auth', 'guestSignup', {user, src, guestKey: readCookie('guestKey')}, (res) => {
      if (is.propertyCount(res.errors, 0)) {
        commit('processLogin', {data: res});
        commit('setUserData', {guest: true});
      }

      resolve(res);
    });
  });
};

/**
 * Complete creating account out of guest mode.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.email  E-mail address.
 * @param  {String} options.pass   Password.
 * @param  {String} options.id     (optional) Social ID.
 * @param  {String} options.type   (optional) Social type.
 * @param  {String} options.gender   (optional) Gender.
 * @param  {String} options.bday    (optional) Birthday.
 * @return {Promise}
 */
export const guestComplete = ({commit}, {email, pass, id, type, gender, bday}) => {
  return new Promise((resolve) => {
    request('auth', 'guestComplete', {email, pass, id, type, gender, bday}, (res) => {
      if (is.propertyCount(res.errors, 0)) {
        const expires = new Date(Date.now() + 31536000000).toGMTString();
        document.cookie = `lastLogin=${email}; expires=${expires}; path=/`;

        // Update their user data.
        commit('setUserData', {email, guest: false});
      }

      resolve(res);
    });
  });
};

/**
 * Complete the second step of guest signup with birthday & gender.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.bday  Birthday.
 * @param  {String} options.gender   Gender.
 * @return {Promise}
 */
export const guestComplete2 = ({commit}, {bday, gender}) => {
  return new Promise((resolve) => {
    request('auth', 'guestComplete2', {bday, gender}, (res) => {
      resolve(res);
    });
  });
};

/**
 * Mark a player as consenting to GDPR, etc.
 * @param  {Function} options.commit Perform a mutation.
 * @return {Promise}
 */
export const privacyConsent = ({commit}) => {
  return new Promise((resolve) => {
    request('auth', 'privacyConsent', {}, (res) => {
      resolve(res);
    });
  });
};

/**
 * Send notice to the server whether this player is away or not.
 * @param  {Object} options.state Full state reference.
 * @param  {Boolean} away          Whether away or not.
 */
export const setAway = ({state}, away) => {
  if (state.user.auth) {
    send('auth', 'away', {away});
  }
};

/**
 * Load the list of players for the directory.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} options.page   Page to load.
 */
export const loadDirectory = ({commit}, {page, query}) => {
  request('directory', 'load', {page, query}, (data) => {
    if (data.players) {
      commit('setDirectoryPlayers', data.players);
    }
  });
};

/**
 * Send the contact us page data to the server to send the e-mail.
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {String} options.email   E-mail from.
 * @param  {String} options.subject E-mail subject.
 * @param  {String} options.message E-mail message.
 */
export const contactUs = ({commit}, {email, subject, message}) => {
  send('contact', 'send', {email, subject, message});
};

/**
 * Search the forums.
 * @param  {Function} options.commit Performs a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String}   query          Search query.
 * @param  {String}   gid            Category to search. (Searches all by default)
 * @param  {Number}   Limit          The max to load per page.
 * @param  {Object}   key            Sort key for paging.
 * @param  {String}   direction      Whether to load the 'next' or 'previous' page.
 */
export const searchForums = ({commit, state}, {gid, limit, key, direction}) => {
  const query = state.forums.searchQuery;

  return new Promise((resolve) => {
    request('forums', 'search', {query, gid, limit, key, dir: direction}, (data) => {
      commit('setForumThreads', {items: data.threads, concat: true, prepend: direction === 'prev'});
      resolve({items: data.threads, end: data.end});
    });
  });
};

/**
 * send forum thread to server to be created and broadcast thread creation
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 */
export const createForumThread = ({commit, state}) => {
  if (state.editor.value && state.editor.value !== '' &&
    state.editor.value !== '<div><br></div>' &&
    state.editor.title !== '') {
    const tags = state.editor.selectedTags
      .map(tag => state.forums.tags.names.indexOf(tag))
      .filter(tag => typeof tag === 'number');

    if (!tags || tags.length === 0) {
      return;
    }

    const data = {
      html: state.editor.value,
      title: state.editor.title,
      gid: state.forums.currentCategory,
      tags,
      poll: state.editor.poll,
    };

    commit('toggleEditor', {type: 'new'});
    commit('setSelectedEditorTags', []);
    commit('setEditorPoll');
    commit('setEditorView', 'editor');
    commit('toggleEditorPoll', false);
    commit('toggleShowTags', {show: false});

    send('forums', 'createThread', data);
  }
};

/**
 * Send forum thread reply to server to be created and broadcast reply creation.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 */
export const postForumReply = ({commit, state}) => {
  const value = state.editor.value;
  if (value && value !== '' && value !== '<div><br></div>') {
    const data = {
      html: state.editor.value,
      gid: state.forums.currentCategory,
      tid: state.forums.activeThread.id,
    };

    commit('toggleEditor', {type: 'reply'});

    send('forums', 'updateThreadView', {tid: state.forums.activeThread.id});
    send('forums', 'postReply', data);
  }
};

/**
 * Send forum thread post to server to be edited and broadcast post edit.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String} pid            Unique post identifier.
 */
export const editForumPost = ({commit, state}, pid) => {
  const value = state.editor.value;
  if (value && value !== '' && value !== '<div><br></div>') {
    const data = {
      html: state.editor.value,
      pid,
    };

    commit('toggleEditor', {type: 'edit'});

    send('forums', 'updateThreadView', {tid: state.forums.activeThread.id});
    send('forums', 'editPost', data);
  }
};

/**
 * Switch to the thread list view of a category and update the category views.
 * @param  {Function} options.commit Performs a mutation.
 * @param  {Number} gid            Game/forum category ID.
 */
export const viewThreads = ({commit, state}, gid) => {
  commit('setForumCategory', gid);
  commit('changeView', 'forum-threads-list-view');

  if (state.user.auth) {
    send('forums', 'viewCategory', {gid});
  }
};

/**
 * Load the list of forum threads from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} gid            The game ID (this forum category).
 * @param  {Number} page           The page to load.
 * @param  {Number} Limit          The max to load per page.
 * @param  {Array} tags            The tags to filter by.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const loadForumThreads = ({commit, state}, {gid, page, limit, tags, key, direction}) => {
  if (state.forums.searchIsVisible && state.forums.searchQuery !== '') {
    return searchForums({commit, state}, {gid, limit, key, direction});
  }

  return new Promise((resolve) => {
    request('forums', 'loadThreadList', {gid, page, limit, tags, key, dir: direction}, (data) => {
      commit('setUserData', {forumban: data.ban});

      if (data.threads) {
        commit('setForumThreads', {items: data.threads, concat: true, prepend: direction === 'prev'});
      }

      resolve({items: data.threads, end: data.end});
    });
  });
};

/**
 * Load the list of threads to which user is subscribed from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} limit
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const loadForumSubscriptions = ({commit, state}, {limit, key, direction}) => {
  return new Promise((resolve) => {
    request('forums', 'loadSubscriptions', {limit, key, dir: direction}, (data) => {
      if (data.threads) {
        commit('setForumThreads', {items: data.threads, concat: true, prepend: direction === 'prev'});
      }

      resolve({items: data.threads, end: data.end});
    });
  });
};

/**
 * Subscribe user to a forum thread.
 * @param  {Object}   options
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Function} options.state  Full state reference.
 * @param  {Number}   tid            The thread ID.
 */
export const subscribeToThread = ({commit, state}, {tid}) => {
  const button = state.panel.buttons[1];

  button.loading = true;
  commit('setPanelData', state.panel);

  request('forums', 'subscribe', {tid}, () => {
    button.loading = false;
    button.label = 'Unsubscribe';
    button.view.action = 'unsubscribeFromThread';

    commit('setPanelData', state.panel);
  });
};

/**
 * Unsubscribe user from a forum thread (from ForumSubscriptionsView)
 * @param  {Object}   options
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Function} options.state  Full state reference.
 * @param  {Number}   tid            The thread ID.
 */
export const unsubscribeFromSubscriptions = ({commit, state}, {tid}) => {
  // Instruct server to unsubscribe user.
  // TODO: Should we show a loading animation here, or just remove the thread immediately?
  request('forums', 'unsubscribe', {tid}, () => {
    // Remove thread from list.
    const threadIndex = state.forums.threads.findIndex(thread => thread._id === tid);
    state.forums.threads.splice(threadIndex, 1);
    const items = state.forums.threads;
    commit('setForumThreads', {items});
  });
};

/**
 * Unsubscribe user from a forum thread (from ThreadView).
 * @param  {Object}   options
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Function} options.state  Full state reference.
 * @param  {Number}   tid            The thread ID.
 */
export const unsubscribeFromThread = ({commit, state}, {tid}) => {
  const button = state.panel.buttons[1];

  button.loading = true;
  commit('setPanelData', state.panel);

  request('forums', 'unsubscribe', {tid}, () => {
    button.loading = false;
    button.label = 'Subscribe';
    button.view.action = 'subscribeToThread';

    commit('setPanelData', state.panel);
  });
};

/**
 * View a forum thread.
 * @param  {Object}   options
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number}   tid            The thread ID.
 * @param  {Number}   limit
 * @param  {Number}   gid            The category/game ID.
 */
export const viewThread = ({commit, state}, {tid, limit, gid, pid}) => {
  commit('togglePanelLoading', true);

  return new Promise((resolve) => {
    request('forums', 'viewThread', {tid, limit, gid, pid, reload: state.forums.reload}, (data) => {
      commit('togglePanelLoading', false);
      commit('setActiveThreadId', tid);
      commit('setActiveThreadPostCount', data.postCount);

      if (data.poll) {
        commit('setThreadPollData', data.poll);
      }

      resolve(data);
    });
  });
};

/**
 * Update the view time for a thread to now.
 * @param  {Object}   options
 * @param  {Number}   tid      The thread ID.
 */
export const updateThreadView = ({commit}, {tid}) => {
  request('forums', 'updateThreadView', {tid});
};

/**
 * Load the list of forum posts from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} tid            The thread ID.
 * @param  {Number} page           The page to load.
 * @param  {Number} limit          The number of items per page.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 * @param  {Number} limit
 */
export const loadForumPosts = ({commit, state}, {tid, page, limit, key, direction}) => {
  return new Promise((resolve) => {
    request('forums', 'loadPosts', {tid, page, key, dir: direction, limit}, (data) => {
      if (data.posts) {
        commit('setForumPosts', {items: data.posts, concat: true, prepend: direction === 'prev'});
      }

      resolve({items: data.posts, end: data.end});
    });
  });
};

/**
 * Send request to server to toggle sticky/locked status.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.tid    Forum thread ID.
 * @param  {String} options.type   'sticky' or 'locked'.
s */
export const toggleForumThreadStatus = ({commit}, {tid, type}) => {
  return new Promise((resolve) => {
    request('forums', 'toggleThreadStatus', {tid, type}, (data) => {
      resolve(data);
    });
  });
};

/**
 * Send a request to the server to delete a forum post or thread.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String}   id             Post ID to delete.
 * @param  {String}   player         Owner of post.
 */
export const deleteForumPost = ({commit}, {id, player}) => {
  send('forums', 'deletePost', {id, player});
};

/**
 * Send a request to the server to delete a forum post or thread.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String}   id             Post ID to delete.
 * @param  {String}   uid            User who posted this update.
 */
export const deleteSocialFeedUpdate = ({commit}, {id, uid}) => {
  send('socialFeed', 'deleteUpdate', {id, uid});
};

/**
 * Send a request to the server to delete a forum post or thread.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String}   id             Post ID the comment is on.
 * @param  {String}   uuid           User who posted the update.
 * @param  {String}   cuid           User who posted the comment.
 * @param  {String}   t              Time the comment was posted.
 */
export const deleteSocialFeedComment = ({commit}, {id, uuid, cuid, t}) => {
  send('socialFeed', 'deleteComment', {id, uuid, cuid, t});
};

/**
 * Mod tool to toggle a user's forum ban status.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} uid            User ID to ban.
 */
export const toggleForumBan = ({commit}, uid) => {
  request('forums', 'toggleBan', {uid}, (res) => {
    commit('setProfileForumBan', res.ban || 0);
  });
};

/**
 * Submit a vote on a thread's poll.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String} options.id     Thread ID.
 * @param  {Number} options.answer Answer index.
 */
export const voteThreadPoll = ({commit, state}, {id, answer}) => {
  // Send the vote to the server.
  send('forums', 'vote', {id, answer});

  // Immediately show the poll results.
  commit('setThreadPollData', Object.assign({}, state.forums.activeThread.poll, {voted: true}));
};

/**
 * Load the list of chat messages from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Number} Limit          Limit to load in the page.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const loadChatThreadList = ({commit, state}, {limit, key, direction}) => {
  const listIsEmpty = !state.chat.messages.length;

  return new Promise((resolve) => {
    request('chat', 'loadList', {limit, key, dir: direction}, (data) => {
      if (data.chats) {
        commit('setChatThreadList', {items: data.chats, concat: !listIsEmpty, prepend: direction === 'prev'});

        // Setup the player status state and subscribe to updates.
        const players = data.chats
          .map(chat => chat.players)
          .reduce((acc, cur) => [...acc, ...cur], []);

        // Set and subscribe to player status.
        if (players && players.length > 0) {
          commit('setPlayerStatus', players);
          bulkSubscribe(players.map(player => `playerStatus:${player.uid}`));
        }

        // Subscribe to the chat list updates.
        subscribe(`updateChatList:${state.user.uid}`);
      }

      resolve({items: data.chats, end: data.end});
    });
  });
};

/**
 * Get the messages of a chat thread from the server and display in the view.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} options.limit  The limit to load.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const loadChatThread = ({state, commit}, {id, limit, key, direction}) => {
  return new Promise((resolve) => {
    request('chat', 'loadThread', {id, limit, key, dir: direction}, (data) => {
      if (data.messages) {
        commit('setChatThread', {
          items: data.messages,
          players: data.players,
          concat: true,
          prepend: direction === 'prev',
          ignore: data.ignore || false,
          name: data.name,
        });
      }

      resolve({items: data.messages, end: data.end});
    });
  });
};

/**
 * Send a request to the server to start a new thread or send back the current one.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} uid            User ID to start chat with.
 */
export const startChatThread = ({commit}, uid) => {
  // Show loading display.
  commit('togglePanelLoading');
  commit('setFriendsList', {items: [], concat: false});

  // Send request to server to start new thread.
  request('chat', 'newThread', {uid}, ({chat}) => {
    commit('togglePanelLoading');

    if (chat) {
      // Display the chat thread view.
      commit('resetChatThread');
      commit('viewChatMessage', {
        id: chat._id,
        user: chat.user,
        uid: chat.uid,
      });

      // Update the view and reset the back-button history.
      commit('changeView', 'chat-message-view');
      commit('setHistory', ['chat-list-view']);
    }
  });
};

/**
 * Send a chat message in the background (for example, for CA new player messages).
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {String} options.uid     User ID to send to.
 * @param  {String} options.message Chat message.
 */
export const sendBackgroundChatMessage = ({commit}, {uid, message}) => {
  // Setup/get the chat thread.
  request('chat', 'newThread', {uid}, ({chat}) => {
    // Send the chat message.
    send('chat', 'sendMessage', {
      html: message,
      cid: chat._id,
    });
  });
};

/**
 * Add a player to a group chat.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String} uid            User to add to group.
 */
export const addGroupChat = ({commit, state}, uid) => {
  // Show loading display.
  commit('togglePanelLoading');
  commit('setFriends', {items: [], concat: false});

  // Send request to server to start new thread.
  request('chat', 'newGroup', {uid, cid: state.chat.activeMessage.id}, ({chat}) => {
    commit('togglePanelLoading');

    if (chat) {
      // Display the chat thread view.
      commit('resetChatThread');
      commit('viewChatMessage', {
        id: chat._id,
        user: chat.user,
        uid: chat.uid,
      });

      // Update the view and reset the back-button history.
      commit('changeView', 'chat-message-view');
      commit('setHistory', ['chat-list-view']);
    }
  });
};

/**
 * Remove a player from a group chat.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String} uid            User ID to remove.
 */
export const remGroupChat = ({commit, state}, uid) => {
  // Show loading display.
  commit('togglePanelLoading');
  commit('setChatSettingsPlayers', []);

  // Send request to server to remove player from group.
  request('chat', 'remGroup', {uid, cid: state.chat.activeMessage.id}, () => {
    commit('togglePanelLoading');

    // If you are removing yourself, go back to the chat list.
    if (uid === state.user.uid) {
      commit('changeView', 'chat-list-view');
    } else {
      goBackView({commit, state});
    }
  });
};

/**
 * Send request to server to prepare the S3 policy.
 * @param  {Function}   options.commit Perform a mutation.
 * @param  {String}     type          Mime type of the image.
 * @param  {String}     bucket        Bucket to upload file.
 */
export const prepareImageUpload = ({commit}, {type, bucket}) => (
  new Promise((resolve) => {
    request('editor', 'prepareUpload', {
      type,
      expires: Date.now() + 3600000,
      bucket: bucket || 'img.goldfire.me',
    }, (data) => {
      resolve(data);
    });
  })
);

/**
 * Send request to server to have Imagify compress the image and generate thumbnail.
 * @param  {Function}   options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String}     filename      Filename of the image to compress.
 * @param  {String}     bucket      Bucket to upload to.
 */
export const compressImage = ({commit, state}, {filename, bucket}) => (
  new Promise((resolve) => {
    request('editor', 'compressImage', {filename, bucket, type: state.toolbar.activeTab}, (data) => {
      resolve(data);
    });
  })
);

/**
 * Send chat message to the server to be stored and broadcast.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 */
export const sendChatMessage = ({commit, state}) => {
  if (state.editor.value && state.editor.value !== '' && state.editor.value !== '<div><br></div>') {
    request('chat', 'sendMessage', {
      html: state.editor.value,
      cid: state.chat.activeMessage.id,
    }, (data) => {
      if (data.ignore) {
        commit('setChatThread', {ignore: data.ignore});
        commit('toggleEditor', {type: 'chat'});
      }
    });

    commit('setEditorReset', true);
  }
};

/**
 * Send a notice to other clients that this user is typing or not typing.
 * @param  {Object} options.state Full state reference.
 * @param  {Boolean} typing        Whether typing or not typing.
 */
export const chatTyping = ({state}, typing) => {
  send('chat', 'sendMessage', {
    html: '',
    cid: state.chat.activeMessage.id,
    typing,
  });
};

/**
 * Load the list of online friends from the server.
 * @param  {Function} options.commit Perform a mutation.
 */
export const loadOnlineFriends = ({commit}) => {
  request('chat', 'loadOnlineFriends', {}, (data) => {
    if (data.friends) {
      commit('setOnlineFriends', data.friends);
    }
  });
};

/**
 * Load the list of friends from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Number} Limit          Limit to load in the page.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const loadFriends = ({commit, state}, {limit, key, direction}) => {
  const search = state.panel.input && state.panel.input.value ? state.panel.input.value : '';
  const type = state.chat.friendListType;
  const exclude = state.chat.friendListExclude;

  return new Promise((resolve) => {
    const cache = Object.keys(state.friends.cache).length === 0;
    request('chat', 'loadFriends', {limit, key, dir: direction, cache, search, type, exclude}, (data) => {
      // Update the cache and player status.
      if (data.cache) {
        commit('setFriendsCache', data.cache);
        commit('setPlayerStatus', Object.keys(data.cache).map(key => data.cache[key]));
      }

      // Update the friends list
      let items = [];
      if (data.friends) {
        items = data.friends.map(uid => state.friends.cache[uid]);

        commit('setFriendsList', {
          items,
          concat: true,
          prepend: direction === 'prev',
        });

        // Subscribe to status updates from friends.
        send('chat', 'subFriendsList');
      }

      resolve({items, end: data.end});
    });
  });
};

/**
 * Bulk unsubscribe from the friends list.
 */
export const unsubFriendsList = () => {
  send('chat', 'unsubFriendsList');
};

/**
 * Send a request to the server to get the list of products for this game.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} gid            Game ID.
 */
export const getShopProducts = ({commit}, gid) => {
  commit('togglePanelLoading', true);

  request('shop', 'load', {gid}, (data) => {
    commit('togglePanelLoading', false);
    commit('setShopProducts', data.shop);
    commit('setShopCards', data.cards);
  });
};

/**
 * Get the credit cards this player has stored.
 * @param  {Function} options.commit Perform a mutation.
 */
export const getShopCards = ({commit}) => {
  request('shop', 'getCards', {}, (data) => {
    commit('setShopCards', data.cards);
  });
};

/**
 * Get the active subscriptions for this player.
 * @param  {Function} options.commit Perform a mutation.
 */
export const getShopSubscriptions = ({commit}) => {
  request('shop', 'getSubscriptions', {}, (data) => {
    commit('setShopSubscriptions', data.subs);
  });
};

/**
 * Get the details from the serve ron a product and launch checkout window.
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {Number} options.gid     Game ID.
 * @param  {String} options.product Product ID.
 */
export const purchaseProduct = ({commit, state}, {gid, product}) => {
  request('shop', 'getProduct', {gid, product}, (data) => {
    commit('selectShopProduct', {product: data.product, gid, error: data.error});
    commit('setTabView', {tab: 'shop', view: 'shop-payment-view'});
  });
};

/**
 * Send a request to the server to process a credit card payment.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} data           Purchase data.
 * @return {Promise}
 */
export const processCreditCard = ({commit}, data) => {
  return new Promise((resolve) => {
    request('shop', 'processStripe', data, (res) => {
      resolve(res);
    });
  });
};

/**
 * Setup the initial PayPal transaction before sending them to checkout.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} data           Payment data.
 * @return {Promise}
 */
export const setupPaypal = ({commit}, data) => {
  return new Promise((resolve) => {
    request('shop', 'setupPaypal', data, (res) => {
      resolve(res);
    });
  });
};

/**
 * Get the Bitcoin source to charge.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} amount         Amount in USD (in cents).
 * @return {Promise}
 */
export const getBitcoinSource = ({commit}, amount) => {
  return new Promise((resolve) => {
    request('shop', 'getBitcoinSource', {amount}, (data) => {
      resolve(data);
    });
  });
};

/**
 * Send a request to the server to get an Xsolla access token.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} data           Data to use in the token.
 * @return {Promise}
 */
export const getXsollaToken = ({commit}, data) => {
  return new Promise((resolve) => {
    request('shop', 'getXsollaToken', data, (data) => {
      resolve(data);
    });
  });
};

/**
 * Send a request to the server to create a Steam transaction.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} data           Purchase data.
 * @return {Promise}
 */
export const createSteamPayment = ({commit}, data) => {
  return new Promise((resolve) => {
    request('shop', 'createSteamPayment', data, (res) => {
      resolve(res);
    });
  });
};

/**
 * Send a request to the server to process a steam payment.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} data           Purchase data.
 * @return {Promise}
 */
export const processSteamPayment = ({commit}, data) => {
  return new Promise((resolve) => {
    request('shop', 'processSteam', data, (res) => {
      resolve(res);
    });
  });
};

/**
 * Send a request to the server to process a mobile payment.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} data           Purchase data.
 * @return {Promise}
 */
export const processMobilePayment = ({commit}, data) => {
  return new Promise((resolve) => {
    request('shop', 'processMobile', data, (res) => {
      resolve(res);
    });
  });
};

/**
 * Send a request to the server to search for usernames in this game.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.query  Search query.
 * @param  {Number} options.gid    Game ID.
 */
export const searchUsernames = ({commit}, {query, gid}) => {
  return new Promise((resolve) => {
    request('shop', 'searchUsernames', {query, gid}, (data) => {
      resolve(data.usernames);
    });
  });
};

/**
 * Send a request to the server to verify if the username is valid.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} user           Username to check.
 * @return {Promise}
 */
export const verifyUsername = ({commit}, user) => {
  return new Promise((resolve) => {
    request('shop', 'verifyUsername', {user}, (data) => {
      resolve(data);
    });
  });
};

/**
 * Send a request to the server to get the player's sales history.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state           Full state reference.
 * @param  {Number} options.limit  Limit per page.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 * @return {Promise}
 */
export const getShopHistory = ({commit, state}, {limit, key, direction}) => {
  return new Promise((resolve) => {
    request('shop', 'getHistory', {limit, key, dir: direction}, (res) => {
      commit('setShopHistory', {items: res.sales, concat: true, prepend: direction === 'prev'});
      resolve({items: res.sales, end: res.end});
    });
  });
};

/**
 * Load the posts in a specific forum thread and load into the client's data.
 * @param  {Object} options
 * @param  {Function} options.commit        Perform a mutation.
 * @param  {Object} options.state           Full state reference.
 * @param  {Object} data                    Data about the thread to load.
 * @param  {String} data._id                Thread ID.
 * @param  {Number} data.postCount          Number of posts.
 * @param  {Boolean} data.subscribed        Whether user is subscribed to this thread.
 * @param  {Boolean} data.shouldAddHistory  Whether this view should be stored in panel history.
 */
export const viewForumThread = ({commit, state}, {_id, postCount, subscribed, shouldAddHistory = true}) => {
  commit('setActiveThreadId', _id);
  if (state.user.auth) {
    if (shouldAddHistory) {
      commit('toggleTabView', {tab: 'forums', view: 'forum-thread-view'});
    } else {
      commit('setTabView', {tab: 'forums', view: 'forum-thread-view'});
    }
    commit('setPanelData', {
      buttons: [],
      wide: true,
      headerView: 'forum-thread-header',
      footerView: 'forum-thread-footer',
    });
  }

  // Update the state of the thread view.
  const updateState = (res) => {
    commit('setActiveThreadTitle', res.title);
    commit('setActiveThreadLocked', res.locked);
    commit('setActiveThreadPostCount', postCount);

    const label = res.subscribed ? 'Unsubscribe' : 'Subscribe';
    const action = res.subscribed ? 'unsubscribeFromThread' : 'subscribeToThread';

    // Determine which buttons to show.
    const buttons = [{label, loading: false, view: {action, data: {tid: res._id}}}];

    if (!res.locked && !state.user.forumban) {
      buttons.unshift({
        label: 'Post Reply',
        loading: false,
        view: {mutation: 'toggleEditor', data: {type: 'reply'}},
      });
    }

    // Update the panel data.
    commit('setPanelData', {
      title: res.title,
      buttons,
      wide: true,
      headerView: 'forum-thread-header',
      footerView: 'forum-thread-footer',
    });
  };

  // Update state instantly if we have the data, otherwise go get it.
  // if (subscribed) {
  //   updateState({_id, postCount, subscribed});
  // } else {
    request('forums', 'loadMeta', {tid: _id}, (res) => {
      commit('setUserData', {forumban: res.ban});
      updateState(res);
    });
  // }
};

/**
 * Get the current total count of unread threads in categories.
 * @param  {Function} options.commit Perform a mutation.
 */
export const getForumCounter = ({commit}) => {
  request('forums', 'getUnreadCounter', {}, (res) => {
    commit('setForumCounter', res.counter || 0);
  });
};

/**
 * Load the posts in a specific forum thread and load into the client's data.
 * @param  {Function} options.commit Perform a mutation.
 */
export const viewForumCategories = ({commit}) => {
  request('forums', 'loadCategoryList', {}, ({categoryList}) => {
    commit('setForumCategoryList', {categoryList});
  });
};

/**
 * Format and commit the data from the server for updates.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Object} options.res    Updates data from the server.
 * @return {Object}                Formatted update data.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const commitSocialFeed = ({commit, state}, {res, direction}) => {
  const updates = res.updates.map(u => Object.assign(u, {commenting: false}));
  const likeChannels = [];
  const commentChannels = [];
  updates.forEach((update) => {
    likeChannels.push(`toggleLike:${update._id}`);
    commentChannels.push(`updateCommented:${update._id}`);
  });

  bulkSubscribe(likeChannels);
  bulkSubscribe(commentChannels);

  commit('setSocialFeedUpdates', {items: updates, concat: true, prepend: direction === 'prev'});
  commit('incSocialFeedCounter', -state.socialFeed.counter);

  return updates;
};

/**
 * Load the social feed data from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number}   options.limit  Limit to load.
 * @param  {Number}   options.id     ID of user to load social feed for. Defaults to player who requested load.
 *                                   If a user ID is provided, we will not merge in friends' feeds.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 * @return {Promise}
 */
export const getSocialFeed = ({commit, state}, {limit = 0, id, key, direction}) => {
  return new Promise((resolve) => {
    request('socialFeed', 'load', {limit, id, key, dir: direction}, (res) => {
      const updates = commitSocialFeed({commit, state}, {res, direction});
      resolve({items: updates});
    });
  });
};

/**
 * Get a single social feed update from the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String} options.id     Update ID.
 */
export const getSocialFeedUpdate = ({commit, state}, {id}) => {
  request('socialFeed', 'loadUpdate', {id}, (res) => {
    commitSocialFeed({commit, state}, {res, page: 0});
  });
};

/**
 * Get the current social feed unread counter.
 * @param  {Function} options.commit Perform a mutation.
 */
export const getSocialFeedCounter = ({commit}) => {
  request('socialFeed', 'counter', {}, (res) => {
    commit('incSocialFeedCounter', res.counter || 0);
  });
};

/**
 * Set the last time this player viewed the social feed.
 */
export const setLastSocialFeedView = () => {
  send('socialFeed', 'lastView');
};

/**
 * Save a new update in the social feed.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Object} data           Data about comment to submit
 */
export const postSocialUpdate = ({commit, state}) => {
  if (state.editor.value && state.editor.value !== '' && state.editor.value !== '<div><br></div>') {
    send('socialFeed', 'postUpdate', {
      html: state.editor.value,
    });

    commit('setEditorReset', true);
    commit('toggleEditor', {type: 'feed'});
  }
};

/**
 * Save a comment to an update in the social feed.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Object} data           Data about comment to submit
 */
export const submitUpdateComment = ({commit, state}, data) => {
  send('socialFeed', 'submitComment', data);
};

/**
 * Save a like/unlike to an update in the social feed
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {Object} options.state     Full state reference.
 * @param  {Object} options.updateId  Update on which to toggle like.
 */
export const toggleLike = ({commit, state}, {updateId}) => {
  send('socialFeed', 'toggleLike', {updateId});
};

/**
 * Send a request to the server to delete a credit card.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Number} index          Card index.
 */
export const deleteCC = ({commit, state}, index) => {
  const card = state.shop.manage.cards[index];
  if (card) {
    send('shop', 'deleteCard', {card: card.id});
    commit('deleteCC', index);
  }
};

/**
 * Send a request to the server to delete a subscription.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Number} index          Subscription index.
 */
export const deleteSubscription = ({commit, state}, index) => {
  const sub = state.shop.manage.subs[index];
  if (sub) {
    request('shop', 'deleteSubscription', {id: sub.id}, (data) => {
      if (data.mobile) {
        commit('toggleTab', 'shop');

        // Determine what webpage to show.
        let manageUrl;
        if (data.mobile === 'iOS' && state.app.type === 'mobile') {
          manageUrl = 'itms-apps://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/manageSubscriptions';
        } else if (data.mobile === 'iOS') {
          window.location = 'https://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/manageSubscriptions';
        } else if (data.mobile === 'Android' && state.app.type === 'mobile') {
          manageUrl = `market://details?id=${data.name}`;
        } else if (data.mobile === 'Android') {
          window.location = `http://play.google.com/store/apps/details?id=${data.name}`;
        }

        // Open the app store or webpage.
        if (manageUrl) {
          window.parent.postMessage({
            action: 'openInBrowser',
            url: manageUrl,
          }, '*');
        }
      }
    });

    commit('deleteSubscription', index);
  }
};

/**
 * Load the initial games launcher data.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 */
export const loadLauncher = ({commit, state}) => {
  request('launcher', 'load', {}, (data) => {
    // Log them out if the user doesn't exist.
    if (data.noUser) {
      window.location = '/bye';
      return null;
    }

    // Update the launcher game data
    commit('setLauncherGames', {games: data.games || []});

    // Subscribe to notifications.
    subscribe(`recNotification:${state.user.uid}`);

    // Subscribe to update unread message notification count.
    subscribe(`setToolbarMessages:${state.user.uid}`);

    // Set the toolbar notice count.
    commit('setToolbarNotifications', data.total);

    // Set the toolbar message count.
    commit('setToolbarMessages', data.msgs);

    // Hack to fix race condition where guest complete sets guest to false,
    // and then this comes back right after setting it back to true.
    if (state.user.guest === 0) {
      data.guest = false;
    }

    // Set the user's notices value.
    commit('setUserData', {
      notices: data.notices,
      guest: data.guest,
      isGdpr: data.isGdpr,
      gdpr: data.gdpr,
      email: data.email || '',
      ltv: data.ltv || 0,
      verify: data.verify,
    });
  });
};

/**
 * Send a request to the server to get updated game rankings.
 * @param  {Function} options.commit Perform a mutation.
 */
export const refreshLauncherRankings = ({commit}) => {
  request('launcher', 'refreshRankings', {}, (data) => {
    commit('setLauncherGamesRank', {games: data || []});
  });
};

/**
 * Load the list of notifications for a game.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Number} options.limit  Number to load.
 * @param  {Object} key            Sort key for paging.
 * @param  {String} direction      Whether to load the 'next' or 'previous' page.
 */
export const loadNotifications = ({commit, state}, {limit, gid, key, direction}) => {
  return new Promise((resolve) => {
    request('notifications', 'load', {limit, gid, key, dir: direction}, (data) => {
      if (data.notifications) {
        commit('setNotifications', {items: data.notifications, concat: true, prepend: direction === 'prev'});
      }

      resolve({items: data.notifications, end: data.end});
    });
  });
};

/**
 * Mark all notifications from this game as read.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} gid            Game ID.
 */
export const markNotificationsRead = ({commit}, gid) => {
  send('notifications', 'allRead', {gid});
};

/**
 * Mark a single notification as read.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} id             Notification ID.
 */
export const markNotificationRead = ({commit}, id) => {
  send('notifications', 'read', {id});
};

/**
 * Send request to the server to connect this player to a game.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {Number} gid            Game ID.
 * @return {Promise}
 */
export const connectGame = ({commit, state}, gid) => {
  return new Promise((resolve) => {
    request('games', 'connect', {gid}, (data) => {
      if (data.connected) {
        commit('setUserData', {
          games: Array.from(new Set([gid, ...state.user.games])),
        });

        // Refresh the launcher list.
        loadLauncher({commit, state});
      }

      resolve(data);
    });
  });
};

/**
 * Update the active game this player is on.
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {Number} options.gid     Game they are on.
 */
export const updateActiveGame = ({commit}, {gid}) => {
  send('games', 'active', {gid});
};

/**
 * Update the steam ID for a player.
 * @param  {Function} options.commit  Perform a mutation.
 * @param  {Number} options.steamid     Steam ID.
 */
export const updateSteamId = ({commit}, {steamid}) => {
  send('auth', 'updateSteamId', {steamid});
};

/**
 * Load the list of leaderboard categories for this game.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 * @param  {String} options.uid    User ID to load.
 * @param  {Number} options.gid    Game ID to load.
 * @return {Promise}
 */
export const loadLeaderboardCategories = ({commit, state}, {uid, gid}) => {
  uid = uid || state.user.uid;
  gid = gid || 0;

  commit('togglePanelLoading', true);
  commit('setLeaderboardCategories', {
    categories: [],
  });

  return new Promise((resolve) => {
    request('leaderboards', 'loadCategories', {uid, gid}, (data) => {
      commit('setLeaderboardCategories', {
        categories: data,
      });
      commit('togglePanelLoading', false);
      resolve(data);
    });
  });
};

/**
 * Load page of players in the leaderboards view.
 * @param  {Function}  options.commit    Perform a mutation.
 * @param  {Object}  options.state     Full state reference.
 * @param  {String}  options.uid       User to load.
 * @param  {String}  options.lid       Leaderboard id.
 * @param  {Number}  options.limit     Limit to load.
 * @param  {Object}  options.key       Sort keys for paging.
 * @param  {String}  options.direction Direction to load.
 * @param  {String}  options.jump      User to jump to.
 * @return {[type]}                    [description]
 */
export const loadLeaderboard = ({commit, state}, {uid, lid, limit, key, direction, jump}) => {
  const allTime = state.leaderboards.allTime;
  const allUsers = state.leaderboards.allUsers;

  return new Promise((resolve) => {
    request('leaderboards', 'loadLeaderboard', {
      uid,
      lid,
      allTime,
      allUsers,
      key,
      dir: direction,
      limit,
      jump,
    }, (data) => {
      commit('setLeaderboardPlayers', {
        items: data.metrics,
        concat: true,
        prepend: direction === 'prev'
      });
      resolve({items: data.metrics, end: data.end});
    });
  });
};

/**
 * Get the online counter for games.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Array} games          Game ID's to get.
 */
export const getGamesOnlineCounters = ({commit}, games) => {
  request('games', 'countOnline', {games}, (res) => {
    commit('setGamesOnline', res || 0);
  });
};

/**
 * Get the list of online players for this game.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} game           Game ID.
 */
export const getOnlinePlayers = ({commit}, game) => {
  request('games', 'getOnline', {game}, (res) => {
    commit('setOnlinePlayers', res.players);
  });
};

/**
 * Initialize player settings on load of GFN.
 * @param  {Function} options.commit Perform a mutation.
 * @return {Promise}
 */
export const initSettings = ({commit}) => {
  request('settings', 'init', {}, (data) => {
    if (data) {
      commit('setSettingsData', data);
    }

    // Apply settings.
    Howler.mute(data.mute);

    // Set the toolbar direction.
    commit('setToolbarDirection', data.left);
  });
};

/**
 * Load the current player settings.
 * @param  {Function} options.commit Perform a mutation.
 * @return {Promise}
 */
export const loadSettings = ({commit}) => {
  commit('togglePanelLoading');

  return new Promise((resolve) => {
    request('settings', 'load', {}, (data) => {
      commit('togglePanelLoading');
      commit('setSettingsData', data);
      resolve(data);
    });
  });
};

/**
 * Save player settings on the server.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 */
export const saveSettings = ({commit, state}) => {
  const button = state.panel.buttons[0];
  const settings = Object.assign({}, state.settings);
  delete settings.error;

  // Show loading indicator on the button.
  button.loading = true;
  commit('setPanelData', state.panel);

  request('settings', 'save', {data: settings}, (data) => {
    commit('setSettingsErrors', data.error);
    button.loading = false;
    commit('setPanelData', state.panel);

    // Update the user notices setting.
    commit('setUserData', {notices: settings.notices});

    // Close the panel if there are no errors.
    let hasErrors = false;
    Object.keys(data.error).forEach((key) => {
      if (data.error[key]) {
        hasErrors = true;
      }
    });
    if (!hasErrors) {
      commit('toggleTab', 'launcher');

      // Update username in state.
      if (settings.user && settings.user.length > 0) {
        commit('setUserData', {user: settings.user});
      }

      // Apply general settings.
      Howler.mute(settings.mute);

      // Flip the toolbar left/right.
      commit('setToolbarDirection', settings.left);
    }
  });
};

/**
 * Send request to the server to delete this account.
 */
export const deleteAccount = () => {
  send('settings', 'delete', {});
};

/**
 * Send a request to the server to resend the verification e-mail.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Object} options.state  Full state reference.
 */
export const resendVerification = ({commit, state}) => {
  send('settings', 'verify', {});

  if (state.user.auth) {
    commit('toggleTab', 'launcher');
  }
};

/**
 * Send a request to the server to do a fuzzy search on country names.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} query          Search string.
 * @return {Promise}
 */
export const searchCountries = ({commit}, query) => {
  return new Promise((resolve) => {
    request('settings', 'searchCountries', {query}, (data) => {
      resolve(data.countries);
    });
  });
};

/**
 * Send a request to the server to load the medals and progress for this game/user.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {Number} gid            Game ID.
 */
export const getGameMedals = ({commit}, gid) => {
  commit('togglePanelLoading', true);

  request('games', 'getMedals', {gid}, (data) => {
    commit('togglePanelLoading', false);
    commit('setLeaderboardMedals', data);
  });
};

/**
 * Unsubscribe this user from e-mail.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.sig    Signature verification.
 * @param  {String} options.email  E-mail of user.
 */
export const unsubscribeEmail = ({commit}, {sig, email}) => {
  send('auth', 'unsubscribeEmail', {sig, email});
};

/**
 * Check if this player should see the review prompt for this game/platform.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.type   'mobile', etc.
 * @param  {Number} options.gid    Game ID.
 */
export const checkReviewPrompt = ({commit}, {type, gid}) => {
  request('settings', 'checkReviewPrompt', {type, gid}, (data) => {
    commit('setUpdates', {reviews: data.show});
  });
};

/**
 * Set the time of the last review prompt for this game/platform.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.type   'mobile', etc.
 * @param  {Number} options.gid    Game ID.
 */
export const setReviewPrompt = ({commit}, {type, gid}) => {
  send('settings', 'setReviewPrompt', {type, gid});
};

/**
 * Handle native Apple login flow.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.id     Apple user ID.
 * @param  {String} options.email  Apple email.
 */
export const appleLogin = ({commit}, {id, email}) => {
  request('passport', 'appleLogin', {id, email}, (data) => {
    window.location = data.url;
  });
};

/**
 * Update the name of a chat group.
 * @param  {Function} options.commit Perform a mutation.
 * @param  {String} options.id   Chat thread ID.
 * @param  {String} options.name    Thread name.
 */
export const setChatGroupName = ({commit}, {id, name}) => {
  send('chat', 'setGroupName', {id, name});
};
